import { useQueries } from '@tanstack/react-query'
import { DuplicatedTerms, getTermDuplicates, qk } from 'api'
import { IconHelperInfo } from 'assets/icons'
import { ToastContent } from 'elements/feedback'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { DuplicatesToastBody } from './duplicates-toast-body'

type Props = {
  projectId: string
  translations: Array<{ lang: string; text: string }>
}

export const useTermDuplicates = ({ projectId, translations }: Props) => {
  const { t } = useTranslation()

  const $termDuplicates = useQueries({
    queries: translations.map(({ lang, text }, index) => ({
      queryKey: qk.terms.duplicates.toKeyWithArgs({ projectId, lang, text }),
      queryFn: () => getTermDuplicates({ projectId, lang, text }),
      enabled: false,
      onSuccess: (termDuplicate: DuplicatedTerms) => {
        const data = termDuplicate
        const translation = translations[index]

        if (data) {
          toast.warning(
            <ToastContent
              title={t('projects.terms.term_duplicates_title')}
              subtitle={t('projects.terms.term_duplicates_subtitle')}
              body={
                <DuplicatesToastBody
                  language={translation?.lang ?? ''}
                  text={translation?.text ?? ''}
                  keys={data?.map((term: { key: any }) => term.key) ?? []}
                />
              }
            />,
            {
              icon: <IconHelperInfo sx={{ color: 'warning.light' }} />,
              autoClose: false,
            },
          )
        }
      },
    })),
  })

  const showDuplicatesMessages = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [_, termDuplicate] of $termDuplicates.entries()) {
      termDuplicate.refetch()
    }
  }

  return {
    showDuplicatesMessages,
  }
}
